// The figure card: a character as data (decisions 0029, 0030; the character-cards suggestion: independent slots,
// clamped ranges, templates as pure data). A card names a joint tree, the pieces built on each joint (lathe shells,
// bands, glyph rows, a hood fitted to a face circle, shaped primitives, radial repeats, chain links), spring chains,
// arms and a hand socket, and its motion tuning. Every number has a range where the look holds: the JSON Schema
// (`figureCardSchema`) states it, the game's content check rejects a value outside it, and `clampCard` clamps at
// build so a card edited live never builds broken. The game owns every card; this file owns only their shape.
//
// Conventions the motion reads (all optional): joints `body`, `head`, `leg.l`, `leg.r`, `arm.l`, `arm.r`,
// `hand.l`, `hand.r`; a four-legged figure's (quad.ts): `body`, `neck`, `head`, `tail`, `leg.<f|h>.<l|r>` and
// `shin.<f|h>.<l|r>`, and for three-segment legs `foot.<f|h>.<l|r>` with a `toe.<f|h>.<l|r>` point on the ground.
// A mirrored piece on a `.l` joint lands on the matching `.r` joint (and back).
// Coordinates: metres, +y up, the figure faces +z, its feet at y = 0.

/** [x, y, z] in metres (or radians for a rotation). */
export type Vec3 = [number, number, number];
/** A profile point [radius, y]. */
export type ProfilePoint = [number, number];
/** "#rrggbb", or a key into the scene's palette (e.g. "hero.red"). */
export type Colour = string;

/** What every piece shares: its colour and facets, its joint, and a mirror copy. */
export interface Surface {
  colour: Colour;
  /** Metres of vertex jitter (0 to 0.1). */
  jitter?: number;
  /** Per-face tint, ± (0 to 0.3). */
  tint?: number;
  /** 0 lit, 1 glowing (0 to 1). */
  glow?: number;
  /** Also build a copy mirrored across x (on the matching `.l`/`.r` joint when the joint has a side). */
  mirror?: boolean;
  /** Offsets the card's seed for this piece's jitter and tint. */
  seed?: number;
}

/** Primitive shapes and what `size` means for each. */
export type ShapeKind = "sphere" | "cone" | "cylinder" | "box" | "icosahedron" | "octahedron" | "dodecahedron" | "tetrahedron" | "torus";

/** A shaped primitive placed in model space. */
export interface ShapeSpec {
  shape: ShapeKind;
  /** sphere [r]; cone [r, h]; cylinder [rTop, rBottom, h]; box [w, h, d]; the polyhedra [r]; torus [R, tube, arc]. */
  size: number[];
  /** Radial (and height) segments for sphere, cone, cylinder and torus. */
  segments?: [number, number];
  /** Subdivision for the polyhedra (0 to 2). */
  detail?: number;
  /** Turned upside down before placing (a cone pointing down). */
  flip?: boolean;
  at?: Vec3;
  /** Euler XYZ, radians. */
  rot?: Vec3;
  scale?: Vec3;
}

/** A body of revolution around y, open at the front by `gap` radians (a split robe, a hood). */
export interface LathePiece extends Surface {
  kind: "lathe";
  joint: string;
  profile: ProfilePoint[];
  segments: number;
  gap?: number;
  /** Points above `from` move back (-z) by `back` per metre of height (a hood's point tipped back). */
  tip?: { from: number; back: number };
}

/** An open ring of cloth (a hem, a trim). */
export interface BandPiece extends Surface {
  kind: "band";
  joint: string;
  /** [bottom, top] radius. */
  radius: [number, number];
  /** The band's centre height. */
  y: number;
  height: number;
  segments: number;
  gap?: number;
}

/** A ring of small flat triangles pointing up and facing out (glyph rows on a hem or capelet). */
export interface GlyphRowPiece extends Surface {
  kind: "glyphRow";
  joint: string;
  radius: number;
  y: number;
  count: number;
  size: number;
  /** Radians either side of the front left empty (a split). */
  skip?: number;
}

/** A hood built ring by ring along a profile, its front opening cut per ring to a face circle (decision 0040). */
export interface HoodPiece extends Surface {
  kind: "hood";
  joint: string;
  profile: ProfilePoint[];
  segments: number;
  /** The face circle the opening hugs: its centre height and radius, and the rings set evenly around it. */
  face: { y: number; radius: number; rings: number };
  /** Extra ring heights (smoother bends). */
  rings?: number[];
  tip?: { from: number; back: number };
  /** Rings above `from` go to another joint (a sprung hood point). */
  split?: { from: number; joint: string };
}

/** A shaped primitive on a joint. */
export interface ShapePiece extends Surface, ShapeSpec {
  kind: "shape";
  joint: string;
}

/** `count` copies of a shape around a circle, each turned to face out, with seeded size and turn variation. */
export interface RadialPiece extends Surface {
  kind: "radial";
  joint: string;
  count: number;
  radius: number;
  y: number;
  item: ShapeSpec;
  /** Each copy scaled by a seeded factor in [min, max]. */
  scaleRange?: [number, number];
  /** Each copy turned by a seeded ± amount per axis (radians). */
  rotRange?: Vec3;
}

/** Glyph shapes for chain links. */
export type GlyphShape = "triangle" | "bars" | "comb" | "diamond";

/** A flat band on every link of a chain (a scarf): edges and a glyph per link. */
export interface LinksPiece extends Surface {
  kind: "links";
  chain: string;
  width: number;
  thickness: number;
  edge?: { colour: Colour; width: number };
  glyphs?: { colour: Colour; glow: number; size: number; shapes: GlyphShape[] };
}

/** Any piece. */
export type Piece = LathePiece | BandPiece | GlyphRowPiece | HoodPiece | ShapePiece | RadialPiece | LinksPiece;

/** A spring chain (a scarf, a hood point): its rest polyline in model space hangs from a joint. */
export interface ChainSpec {
  id: string;
  /** The joint it hangs from. */
  joint: string;
  /** The rest polyline, model space; link i spans points[i] → points[i + 1] and is joint `<id>.<i>`. */
  points: Vec3[];
  /** The links' facing (their local +z) at rest, model space. Default [0, 0, -1] (facing back). */
  facing?: Vec3;
  /** Links it can grow by: built hidden past the rest polyline's end, along its last link; the game shows them with
   *  `Chain.setLinks` (the Wanderer's scarf lengthening with collected glyphs, decision 0053). */
  grow?: { links: number };
  spring: {
    /** Pull toward the rest shape per step (0 to 1). */
    stiffness: number;
    /** Velocity lost per step (0 to 1). */
    damping: number;
    /** m/s² down. */
    gravity: number;
    /** Air drag on velocity relative to the air (per second). */
    drag: number;
    /** Radians a link may swing from its rest direction (as the joint carries it); unset, no limit. A stiff point
     *  that stands up (a hood's tip) needs one: under gravity and drag it is an inverted pendulum. */
    maxAngle?: number;
  };
}

/** A joint: its parent (default the figure's root) and rest position in model space. */
export interface JointSpec {
  id: string;
  parent?: string;
  at: Vec3;
}

/** How the arms show, and where the hands rest when they do (body space). */
export interface ArmsSpec {
  show: "acting" | "always" | "never";
  rest?: { r?: Vec3; l?: Vec3 };
}

/** The hero's procedural motion (the state machine, gait, look-at, lean and bob). */
export interface MotionTuning {
  /** m/s: walking above this, running above `run`. */
  walk: number;
  run: number;
  /** Seconds a step's swing takes at walk and at run. */
  stepTime: [number, number];
  /** Metres a swinging foot lifts. */
  stepHeight: number;
  /** Seconds of velocity the feet land ahead by. */
  lead: number;
  /** How far a leg may stretch past its rest length before the foot must step (1 to 1.6). */
  reach: number;
  /** Body bob per step (m). */
  bob: number;
  /** Forward lean (radians) at run and in a glide. */
  lean: { run: number; glide: number; jump: number };
  /** The head's look-at clamp (radians) and how fast it turns (per second). */
  look: { yaw: number; up: number; down: number; rate: number };
  /** Idle breathing, metres. */
  breath: number;
  /** Hovering figures (a shade, a wisp): height and bob amplitude, metres (0 for walkers). */
  hover?: { height: number; amp: number; rate: number };
}

/** A four-legged figure's motion (quad.ts): gait thresholds and strides, the step, the body, the look, the poses. */
export interface QuadTuning {
  /** m/s: walking above this, trotting above `trot`, galloping above `gallop` (absent: never gallops). */
  walk: number;
  trot: number;
  gallop?: number;
  /** Metres the body travels per gait cycle. */
  stride: { walk: number; trot: number; gallop?: number };
  /** The share of a cycle each foot is planted (defaults: walk 0.7, trot 0.5, gallop 0.38). */
  duty?: { walk?: number; trot?: number; gallop?: number };
  /** Metres a swinging foot lifts at a trot. */
  stepHeight: number;
  /** The body's bob per step (m), and a gallop's pitch swing (radians). */
  bob: number;
  pitchBob?: number;
  /** The head's look-at clamp (radians) and how fast it turns (per second). */
  look: { yaw: number; up: number; down: number; rate: number };
  /** Idle breathing, metres. */
  breath: number;
  /** Seconds a settling step takes at rest (default 0.25). */
  settle?: number;
  /** Radians the neck and head bend down to eat, together (default 0.9; a grazer's muzzle reaches the grass near 1.8). */
  eat?: number;
  /** Radians the body pitches nose-up to sit (absent: it doesn't sit). */
  sit?: number;
  /** The body's height lying down, as a share of its standing height (default 0.4). */
  lie?: number;
  /** The tail's wag: cycles a second and radians either side. */
  wag?: { rate: number; amp: number };
  /** Three-segment legs: radians a planted foot peels up at the end of its stance (default 0.5), and a lifted paw
   *  folds back at the swing's peak (defaults: a forepaw 1.8, tucked under the leg; a hind paw 0.7, hanging). */
  peel?: number;
  fold?: { fore: number; hind: number };
}

/** A figure card. */
export interface FigureCard {
  joints: JointSpec[];
  pieces: Piece[];
  chains?: ChainSpec[];
  arms?: ArmsSpec;
  /** The joint attachments (weapons, props) go on. */
  socket?: string;
  motion?: MotionTuning;
  /** A four-legged figure's motion (its legs `leg.<f|h>.<l|r>` with `shin.<f|h>.<l|r>` under them, and optionally
   *  `foot.<f|h>.<l|r>` and `toe.<f|h>.<l|r>` for three segments). */
  quad?: QuadTuning;
  /** The jitter and tint seed. */
  seed?: number;
  /** Uniform size (0.3 to 3): every position, piece, chain, hand target and grip, built at this scale (a child). */
  scale?: number;
}

/** Something held on the socket: a weapon or a prop. Its pieces are built in its own space, the grip at the origin. */
export interface Attachment {
  /** Where the hand goes (body space) and the attachment's turn (Euler XYZ, body space). */
  grip: { at: Vec3; rot: Vec3 };
  pieces: AttachmentPiece[];
}

/** An attachment's pieces: shapes and radial repeats, no joint. */
export type AttachmentPiece = (Omit<ShapePiece, "joint"> | Omit<RadialPiece, "joint">) & { joint?: string };

// ---------------------------------------------------------------------------------------------------------------
// The schema: draft-07, every number with its range. Shared by the game's content check and `clampCard`.

/** A JSON Schema object (draft-07), loosely typed. */
export type Schema = Record<string, unknown>;

const num = (minimum: number, maximum: number, description?: string): Schema => ({ type: "number", minimum, maximum, ...(description ? { description } : {}) });
const int = (minimum: number, maximum: number, description?: string): Schema => ({ type: "integer", minimum, maximum, ...(description ? { description } : {}) });
const tuple = (items: Schema[], description?: string): Schema => ({ type: "array", items, minItems: items.length, maxItems: items.length, ...(description ? { description } : {}) });
const vec = (lo: number, hi: number, description?: string): Schema => tuple([num(lo, hi), num(lo, hi), num(lo, hi)], description);
const obj = (properties: Record<string, Schema>, required: string[], description?: string): Schema => ({
  type: "object",
  additionalProperties: false,
  required,
  properties,
  ...(description ? { description } : {}),
});
const list = (items: Schema, minItems: number, maxItems: number, description?: string): Schema => ({ type: "array", items, minItems, maxItems, ...(description ? { description } : {}) });

const TAU = Math.PI * 2;
const JOINT_ID = { type: "string", pattern: "^[a-z][a-zA-Z0-9]*(\\.[a-z0-9][a-zA-Z0-9]*)*$" };
const COLOUR = { type: "string", pattern: "^(#[0-9a-fA-F]{6}|[a-z][a-zA-Z0-9]*(\\.[a-zA-Z0-9]+)*)$", description: "#rrggbb, or a palette key" };
const PROFILE = list(tuple([num(0, 2), num(-1, 4)]), 2, 24, "Profile points [radius, y], bottom to top");
const TIP = obj({ from: num(-1, 4), back: num(0, 2) }, ["from", "back"], "Points above `from` move back by `back` per metre");
const GAP = num(0, Math.PI * 1.5, "Radians open at the front");
const SEGMENTS = int(3, 32);

const SURFACE: Record<string, Schema> = {
  colour: COLOUR,
  jitter: num(0, 0.1, "Metres of vertex jitter"),
  tint: num(0, 0.3, "Per-face tint ±"),
  glow: num(0, 1, "0 lit, 1 glowing"),
  mirror: { type: "boolean" },
  seed: int(0, 1_000_000),
};

const SHAPE_PROPS: Record<string, Schema> = {
  shape: { enum: ["sphere", "cone", "cylinder", "box", "icosahedron", "octahedron", "dodecahedron", "tetrahedron", "torus"] },
  size: list(num(0, 4), 1, 3, "sphere [r]; cone [r, h]; cylinder [rTop, rBottom, h]; box [w, h, d]; polyhedra [r]; torus [R, tube, arc]"),
  segments: tuple([int(3, 32), int(1, 32)]),
  detail: int(0, 2),
  flip: { type: "boolean" },
  at: vec(-4, 4),
  rot: vec(-TAU, TAU),
  scale: vec(0.01, 10),
};
const SHAPE = obj(SHAPE_PROPS, ["shape", "size"]);

const piece = (kind: string, props: Record<string, Schema>, required: string[]): Schema =>
  obj({ kind: { const: kind }, ...SURFACE, ...props }, ["kind", "colour", ...required]);

/**
 * One of several object shapes told apart by `kind`: an if/then per kind, so a bad piece reports only its own kind's
 * errors (a oneOf would list every branch's).
 */
const tagged = (branches: Schema[]): Schema => {
  const kinds = branches.map((b) => ((b.properties as Record<string, Schema>).kind as { const: string }).const);
  return {
    type: "object",
    required: ["kind"],
    properties: { kind: { enum: kinds } },
    allOf: branches.map((b, i) => ({ if: { type: "object", required: ["kind"], properties: { kind: { const: kinds[i] } } }, then: b })),
  };
};

const PIECE: Schema = tagged([
    piece("lathe", { joint: JOINT_ID, profile: PROFILE, segments: SEGMENTS, gap: GAP, tip: TIP }, ["joint", "profile", "segments"]),
    piece("band", { joint: JOINT_ID, radius: tuple([num(0, 2), num(0, 2)]), y: num(-1, 4), height: num(0.001, 1), segments: SEGMENTS, gap: GAP }, ["joint", "radius", "y", "height", "segments"]),
    piece("glyphRow", { joint: JOINT_ID, radius: num(0, 2), y: num(-1, 4), count: int(1, 48), size: num(0.005, 0.5), skip: num(0, Math.PI) }, ["joint", "radius", "y", "count", "size"]),
    piece(
      "hood",
      {
        joint: JOINT_ID,
        profile: PROFILE,
        segments: int(3, 24),
        face: obj({ y: num(-1, 4), radius: num(0.03, 0.4), rings: int(3, 16) }, ["y", "radius", "rings"], "The face circle the opening hugs"),
        rings: list(num(-1, 4), 0, 24),
        tip: TIP,
        split: obj({ from: num(-1, 4), joint: JOINT_ID }, ["from", "joint"], "Rings above `from` go to this joint"),
      },
      ["joint", "profile", "segments", "face"],
    ),
    piece("shape", { joint: JOINT_ID, ...SHAPE_PROPS }, ["joint", "shape", "size"]),
    piece(
      "radial",
      { joint: JOINT_ID, count: int(1, 32), radius: num(0, 2), y: num(-1, 4), item: SHAPE, scaleRange: tuple([num(0.1, 3), num(0.1, 3)]), rotRange: vec(0, Math.PI) },
      ["joint", "count", "radius", "y", "item"],
    ),
    piece(
      "links",
      {
        chain: JOINT_ID,
        width: num(0.01, 0.5),
        thickness: num(0.002, 0.1),
        edge: obj({ colour: COLOUR, width: num(0.002, 0.1) }, ["colour", "width"]),
        glyphs: obj({ colour: COLOUR, glow: num(0, 1), size: num(0.01, 0.2), shapes: list({ enum: ["triangle", "bars", "comb", "diamond"] }, 1, 8) }, ["colour", "glow", "size", "shapes"]),
      },
      ["chain", "width", "thickness"],
    ),
]);

const ATTACHMENT_PIECE: Schema = tagged([
  piece("shape", { joint: JOINT_ID, ...SHAPE_PROPS }, ["shape", "size"]),
  piece("radial", { joint: JOINT_ID, count: int(1, 32), radius: num(0, 2), y: num(-1, 4), item: SHAPE, scaleRange: tuple([num(0.1, 3), num(0.1, 3)]), rotRange: vec(0, Math.PI) }, ["count", "radius", "y", "item"]),
]);

const MOTION = obj(
  {
    walk: num(0.01, 3, "m/s: walking above this"),
    run: num(0.5, 15, "m/s: running above this"),
    stepTime: tuple([num(0.06, 1), num(0.06, 1)], "Seconds per swing at walk and run"),
    stepHeight: num(0, 0.4),
    lead: num(0, 1, "Seconds of velocity the feet land ahead by"),
    reach: num(1, 1.6, "Leg stretch before a forced step"),
    bob: num(0, 0.15),
    lean: obj({ run: num(0, 1), glide: num(0, 1.5), jump: num(-1, 1) }, ["run", "glide", "jump"]),
    look: obj({ yaw: num(0, Math.PI), up: num(0, Math.PI / 2), down: num(0, Math.PI / 2), rate: num(0.5, 40) }, ["yaw", "up", "down", "rate"]),
    breath: num(0, 0.05),
    hover: obj({ height: num(0, 3), amp: num(0, 0.5), rate: num(0, 10) }, ["height", "amp", "rate"]),
  },
  ["walk", "run", "stepTime", "stepHeight", "lead", "reach", "bob", "lean", "look", "breath"],
);

const QUAD = obj(
  {
    walk: num(0.01, 3, "m/s: walking above this"),
    trot: num(0.05, 20, "m/s: trotting above this"),
    gallop: num(0.1, 30, "m/s: galloping above this"),
    stride: obj({ walk: num(0.05, 6), trot: num(0.05, 8), gallop: num(0.05, 12) }, ["walk", "trot"]),
    duty: obj({ walk: num(0.5, 0.9), trot: num(0.3, 0.7), gallop: num(0.2, 0.6) }, []),
    stepHeight: num(0, 0.5),
    bob: num(0, 0.2),
    pitchBob: num(0, 0.4),
    look: obj({ yaw: num(0, Math.PI), up: num(0, Math.PI / 2), down: num(0, Math.PI / 2), rate: num(0.5, 40) }, ["yaw", "up", "down", "rate"]),
    breath: num(0, 0.05),
    settle: num(0.1, 1),
    eat: num(0, 2.4),
    sit: num(0, 1.2),
    lie: num(0.1, 1),
    wag: obj({ rate: num(0, 10), amp: num(0, 1.2) }, ["rate", "amp"]),
    peel: num(0, 1.5),
    fold: obj({ fore: num(0, 2.8), hind: num(0, 2.8) }, ["fore", "hind"]),
  },
  ["walk", "trot", "stride", "stepHeight", "bob", "look", "breath"],
);

const CHAIN = obj(
  {
    id: JOINT_ID,
    joint: JOINT_ID,
    points: list(vec(-4, 4), 2, 16),
    facing: vec(-1, 1),
    spring: obj({ stiffness: num(0, 1), damping: num(0, 1), gravity: num(0, 30), drag: num(0, 10), maxAngle: num(0, Math.PI) }, ["stiffness", "damping", "gravity", "drag"]),
    grow: obj({ links: int(0, 16) }, ["links"], "Links it can grow by (shown with Chain.setLinks)"),
  },
  ["id", "joint", "points", "spring"],
);

const CARD_PROPS: Record<string, Schema> = {
  joints: list(obj({ id: JOINT_ID, parent: JOINT_ID, at: vec(-4, 4) }, ["id", "at"]), 1, 64),
  pieces: list(PIECE, 1, 256),
  chains: list(CHAIN, 0, 8),
  arms: obj({ show: { enum: ["acting", "always", "never"] }, rest: obj({ r: vec(-2, 3), l: vec(-2, 3) }, []) }, ["show"]),
  socket: JOINT_ID,
  motion: MOTION,
  quad: QUAD,
  seed: int(0, 1_000_000),
  scale: num(0.3, 3, "Uniform size"),
};

/**
 * The figure card's JSON Schema (draft-07). `extra` adds the game's own fields to the root (an id, a name reference,
 * a tier): their properties and which are required.
 */
export function figureCardSchema(extra: { properties?: Record<string, Schema>; required?: string[]; title?: string; description?: string } = {}): Schema {
  return {
    $schema: "http://json-schema.org/draft-07/schema#",
    title: extra.title ?? "FigureCard",
    ...(extra.description ? { description: extra.description } : {}),
    type: "object",
    additionalProperties: false,
    required: [...(extra.required ?? []), "joints", "pieces"],
    properties: { ...(extra.properties ?? {}), ...CARD_PROPS },
  };
}

/** The attachment's JSON Schema (a weapon or prop on the socket); `extra` as for `figureCardSchema`. */
export function attachmentSchema(extra: { properties?: Record<string, Schema>; required?: string[]; title?: string; description?: string } = {}): Schema {
  return {
    $schema: "http://json-schema.org/draft-07/schema#",
    title: extra.title ?? "Attachment",
    ...(extra.description ? { description: extra.description } : {}),
    type: "object",
    additionalProperties: false,
    required: [...(extra.required ?? []), "grip", "pieces"],
    properties: { ...(extra.properties ?? {}), grip: obj({ at: vec(-2, 3), rot: vec(-TAU, TAU) }, ["at", "rot"]), pieces: list(ATTACHMENT_PIECE, 1, 64) },
  };
}

// ---------------------------------------------------------------------------------------------------------------
// Clamping: every number to its schema range, every list to its length range.

/** A value clamped by `clampCard`, with its path ("pieces/3/segments"). */
export interface Clamped {
  path: string;
  from: unknown;
  to: unknown;
}

/** Walk `value` against `schema`, clamping numbers into their ranges (integers rounded) and cutting long lists. */
function clampWalk(schema: Schema, value: unknown, path: string, out: Clamped[]): unknown {
  if (schema.allOf) {
    // a tagged union: the branch whose `kind` matches, else the value as is
    const kind = (value as { kind?: unknown } | null)?.kind;
    const hit = (schema.allOf as Schema[]).find((b) => ((b.if as Schema | undefined)?.properties as Record<string, Schema> | undefined)?.kind?.const === kind);
    return hit ? clampWalk(hit.then as Schema, value, path, out) : value;
  }
  if ((schema.type === "number" || schema.type === "integer") && typeof value === "number") {
    let v = Number.isFinite(value) ? value : ((schema.minimum as number | undefined) ?? 0);
    if (schema.type === "integer") v = Math.round(v);
    if (typeof schema.minimum === "number" && v < schema.minimum) v = schema.minimum;
    if (typeof schema.maximum === "number" && v > schema.maximum) v = schema.maximum;
    if (v !== value) out.push({ path, from: value, to: v });
    return v;
  }
  if (schema.type === "array" && Array.isArray(value)) {
    let arr = value;
    if (typeof schema.maxItems === "number" && arr.length > schema.maxItems) {
      out.push({ path, from: `${arr.length} items`, to: `${schema.maxItems} items` });
      arr = arr.slice(0, schema.maxItems);
    }
    return arr.map((v, i) => {
      const items = schema.items as Schema | undefined;
      return items ? clampWalk(items, v, `${path}/${i}`, out) : v;
    });
  }
  if (schema.type === "object" && value && typeof value === "object" && !Array.isArray(value)) {
    const props = (schema.properties ?? {}) as Record<string, Schema>;
    const o: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value)) o[k] = props[k] ? clampWalk(props[k]!, v, path ? `${path}/${k}` : k, out) : v;
    return o;
  }
  return value;
}

/**
 * A copy of `card` with every number inside its schema range and every list within its length, plus what moved.
 * The content check rejects an out-of-range card at build; this keeps a card edited live from building broken.
 */
export function clampCard(card: FigureCard): { card: FigureCard; clamped: Clamped[] } {
  const clamped: Clamped[] = [];
  const out = clampWalk(figureCardSchema(), card, "", clamped) as FigureCard;
  return { card: out, clamped };
}

/** As `clampCard`, for an attachment. */
export function clampAttachment(a: Attachment): { attachment: Attachment; clamped: Clamped[] } {
  const clamped: Clamped[] = [];
  return { attachment: clampWalk(attachmentSchema(), a, "", clamped) as Attachment, clamped };
}

/** The schema builders, for the package's other schemas (pose.ts, reaction.ts, ragdoll.ts); not exported from the index. */
export const schemaParts = { num, int, tuple, vec, obj, list, tagged, JOINT_ID, TAU };
